import React, { useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Box,
  Spinner,
  Progress,
  Badge,
  Flex,
  StatGroup,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Heading,
  Grid,
  GridItem,
  Tooltip,
} from '@chakra-ui/react'
import { useRelatorioDeTroca } from '../hooks/RelatorioDeTrocaContext'

export const ModalSumarioTrocas = ({ isOpen, onClose }) => {
  const { trocas, isLoading, error } = useRelatorioDeTroca()

  const estatisticas = useMemo(() => {
    if (!trocas || !trocas.length) return null

    const totalPedidos = trocas.reduce((acc, troca) => acc + Number(troca.qtd_produto), 0)
    const totalTrocas = trocas.reduce((acc, troca) => acc + Number(troca.qtd_troca), 0)
    const percentualTroca = totalPedidos > 0 ? (totalTrocas / totalPedidos) * 100 : 0

    const fornecedores = {}
    trocas.forEach((troca) => {
      const nomeFornecedor = troca.nome_fornecedor || 'Não informado'
      const codigoFornecedor = troca.codigo_fornecedor || nomeFornecedor

      if (!fornecedores[codigoFornecedor]) {
        fornecedores[codigoFornecedor] = {
          nome: nomeFornecedor,
          totalPedidos: 0,
          totalTrocas: 0,
          qtdRegistros: 0,
          produtos: new Set(),
          quantidade_pedidos: Number(troca.quantidade_pedidos) || 0,
          quantidade_itens: Number(troca.quantidade_itens) || 0,
          pedidos_com_troca: Number(troca.pedidos_com_troca) || 0,
          produtos_troca: Number(troca.produtos_troca) || 0,
        }
      }

      fornecedores[codigoFornecedor].totalPedidos += Number(troca.qtd_produto) || 0
      fornecedores[codigoFornecedor].totalTrocas += Number(troca.qtd_troca) || 0
      fornecedores[codigoFornecedor].qtdRegistros += 1
      fornecedores[codigoFornecedor].produtos.add(troca.codigo_produto)
    })

    const fornecedoresArray = Object.values(fornecedores).map((fornecedor) => {
      const percentualTroca = fornecedor.totalPedidos > 0 ? (fornecedor.totalTrocas / fornecedor.totalPedidos) * 100 : 0
      const percentualPedidosComTroca = fornecedor.quantidade_pedidos > 0 ? (fornecedor.pedidos_com_troca / fornecedor.quantidade_pedidos) * 100 : 0
      const percentualItensTrocados = fornecedor.quantidade_itens > 0 ? (fornecedor.totalTrocas / fornecedor.quantidade_itens) * 100 : 0

      return {
        ...fornecedor,
        produtos: fornecedor.produtos.size,
        percentualTroca,
        percentualPedidosComTroca,
        percentualItensTrocados,
      }
    })

    fornecedoresArray.sort((a, b) => b.totalTrocas - a.totalTrocas)

    const totalQuantidadePedidos = fornecedoresArray.reduce((acc, f) => acc + f.quantidade_pedidos, 0)
    const totalQuantidadeItens = fornecedoresArray.reduce((acc, f) => acc + f.quantidade_itens, 0)
    const totalPedidosComTroca = fornecedoresArray.reduce((acc, f) => acc + f.pedidos_com_troca, 0)
    const totalProdutosTroca = fornecedoresArray.reduce((acc, f) => acc + f.produtos_troca, 0)
    const percentualPedidosComTroca = totalQuantidadePedidos > 0 ? (totalPedidosComTroca / totalQuantidadePedidos) * 100 : 0
    const percentualItensTrocadosGeral = totalQuantidadeItens > 0 ? (totalTrocas / totalQuantidadeItens) * 100 : 0

    return {
      totalRegistros: trocas.length,
      totalPedidos,
      totalTrocas,
      percentualTroca,
      totalQuantidadePedidos,
      totalQuantidadeItens,
      totalPedidosComTroca,
      totalProdutosTroca,
      percentualPedidosComTroca,
      percentualItensTrocadosGeral,
      fornecedores: fornecedoresArray,
    }
  }, [trocas])

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Sumário de Trocas</ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign="center" py={10}>
            <Spinner size="xl" color="blue.500" />
            <Text mt={4}>Carregando sumário de trocas...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Sumário de Trocas</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error ? (
            <Box textAlign="center" py={4}>
              <Text color="red.500">Erro ao carregar o sumário. Por favor, tente novamente.</Text>
            </Box>
          ) : estatisticas ? (
            <>
              <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={6}>
                <GridItem colSpan={2}>
                  <Heading size="sm" mb={2}>
                    Visão Geral de Pedidos
                  </Heading>
                </GridItem>

                <Box p={3} borderWidth="1px" borderRadius="lg" bg="blue.50">
                  <Text fontWeight="bold" mb={2}>
                    Total de Pedidos no Período
                  </Text>
                  <StatGroup>
                    <Stat>
                      <StatNumber>{estatisticas.totalQuantidadePedidos}</StatNumber>
                      <StatLabel>Pedidos</StatLabel>
                    </Stat>
                    <Stat>
                      <StatNumber>{estatisticas.totalQuantidadeItens}</StatNumber>
                      <StatLabel>Itens</StatLabel>
                    </Stat>
                  </StatGroup>
                </Box>

                <Box p={3} borderWidth="1px" borderRadius="lg" bg="purple.50">
                  <Text fontWeight="bold" mb={2}>
                    Pedidos com Troca
                  </Text>
                  <StatGroup>
                    <Stat>
                      <StatNumber>{estatisticas.totalPedidosComTroca}</StatNumber>
                      <StatLabel>Pedidos</StatLabel>
                      <StatHelpText>{estatisticas.percentualPedidosComTroca.toFixed(1)}% do total</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatNumber>{estatisticas.totalProdutosTroca}</StatNumber>
                      <StatLabel>Produtos</StatLabel>
                    </Stat>
                  </StatGroup>
                  <Progress
                    mt={2}
                    value={estatisticas.percentualPedidosComTroca}
                    colorScheme={estatisticas.percentualPedidosComTroca > 20 ? 'red' : 'green'}
                    size="sm"
                    borderRadius="md"
                  />
                </Box>
              </Grid>

              <Divider my={4} />

              <StatGroup mb={6}>
                <Stat>
                  <StatLabel>Total de Registros</StatLabel>
                  <StatNumber>{estatisticas.totalRegistros}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Total Qtd. Pedido</StatLabel>
                  <StatNumber>{estatisticas.totalPedidos}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Total Qtd. Troca</StatLabel>
                  <StatNumber>{estatisticas.totalTrocas}</StatNumber>
                  <StatHelpText>{estatisticas.percentualTroca.toFixed(2)}% do total</StatHelpText>
                </Stat>
              </StatGroup>

              <Box mb={4} p={3} borderWidth="1px" borderRadius="lg" bg="blue.50">
                <Flex justifyContent="space-between" align="center" mb={2}>
                  <Text fontWeight="bold">Relação Troca/Pedido</Text>
                  <Badge colorScheme={estatisticas.percentualTroca > 20 ? 'red' : 'green'}>{estatisticas.percentualTroca.toFixed(2)}%</Badge>
                </Flex>
                <Progress
                  value={estatisticas.percentualTroca}
                  colorScheme={estatisticas.percentualTroca > 20 ? 'red' : 'green'}
                  size="sm"
                  borderRadius="md"
                />
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Percentual calculado com base na quantidade de itens trocados em relação à quantidade de itens nos pedidos.
                </Text>
              </Box>

              <Heading size="sm" mb={2}>
                Trocas por Fornecedor
              </Heading>
              <Divider mb={4} />

              <Table variant="simple" size="sm">
                <Thead bg="#378BBF">
                  <Tr>
                    <Th color="white" borderBottom="none" borderRight="none">
                      Fornecedor
                    </Th>
                    <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                      Total Pedidos
                    </Th>
                    <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                      Pedidos c/ Troca
                    </Th>
                    <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                      Produtos c/ Troca
                    </Th>
                    <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                      Qtd. Itens
                    </Th>
                    <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                      Qtd. Troca
                    </Th>
                    <Th color="white" borderBottom="none" isNumeric>
                      % Troca
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {estatisticas.fornecedores.map((fornecedor, index) => (
                    <Tr key={fornecedor.nome} bg={index % 2 === 1 ? 'gray.50' : 'white'}>
                      <Td borderBottom="none">{fornecedor.nome}</Td>
                      <Td isNumeric borderBottom="none">
                        {fornecedor.quantidade_pedidos}
                      </Td>
                      <Td isNumeric borderBottom="none">
                        {fornecedor.pedidos_com_troca}
                        {fornecedor.quantidade_pedidos > 0 && (
                          <Text as="span" fontSize="xs" color="gray.500" ml={1}>
                            ({fornecedor.percentualPedidosComTroca.toFixed(0)}%)
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric borderBottom="none">
                        {fornecedor.produtos_troca}
                      </Td>
                      <Td isNumeric borderBottom="none">
                        {fornecedor.quantidade_itens}
                      </Td>
                      <Td isNumeric borderBottom="none">
                        {fornecedor.totalTrocas}
                      </Td>
                      <Td borderBottom="none">
                        <Tooltip label="% em relação à quantidade pedida">
                          <Flex justify="flex-end" align="center" gap={2}>
                            <Progress
                              value={fornecedor.percentualTroca}
                              size="xs"
                              colorScheme={fornecedor.percentualTroca > 20 ? 'red' : 'green'}
                              width="60px"
                              borderRadius="md"
                            />
                            <Badge colorScheme={fornecedor.percentualTroca > 20 ? 'red' : 'green'}>{fornecedor.percentualTroca.toFixed(1)}%</Badge>
                          </Flex>
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <Text>Nenhum dado de troca disponível para mostrar.</Text>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
